// EWM-360 — single source of truth for the Add to Cart button's enabled
// state and label, shared by the PDP cart CTA and the PLP/Search product tile.
//
// Rules (confirmed with Luke 2026-06-13):
//   - Out-of-stock products are normally disabled.
//   - Customers whose BusinessUnit has `back-orders-allowed` may add OOS
//     products: the button is ENABLED and relabelled "Back Order". Checkout
//     does not enforce stock, so these lines complete normally.
//   - Products with the `GO` lifecycle code ("removed from sale") are ALWAYS
//     disabled for everyone, regardless of stock and ignoring back-order.
//   - A missing price always disables (and wins the label) — we never let a
//     user buy at an unknown price, and back-order can't rescue it.

export type AddToCartLabelState = 'available' | 'back-order' | 'out-of-stock' | 'price-unavailable';

export interface AddToCartAvailabilityInput {
  /** CT-resolved stock for the active SKU / supply channel. `undefined` = unknown → treated as OOS. */
  inStock?: boolean;
  /** CT supplied no stock signal at all (missing inventory / channel / pre-Suspense fallback). */
  stockUnavailable?: boolean;
  /** CT supplied no price — the button can never be enabled, even for back-order customers. */
  priceUnavailable?: boolean;
  /** Lifecycle code `GO` — removed from sale, always disabled. */
  removedFromSale?: boolean;
  /** BusinessUnit `back-orders-allowed` flag for the current customer. */
  backOrdersAllowed?: boolean;
}

export interface AddToCartAvailability {
  /** Whether the Add to Cart button should render disabled. */
  disabled: boolean;
  /** Which label the button shows; consumers map this to their own i18n keys. */
  labelState: AddToCartLabelState;
  /** True when an OOS product is purchasable via back-order (enabled + "Back Order"). */
  isBackOrder: boolean;
}

export const resolveAddToCartAvailability = ({
  inStock,
  stockUnavailable = false,
  priceUnavailable = false,
  removedFromSale = false,
  backOrdersAllowed = false,
}: AddToCartAvailabilityInput): AddToCartAvailability => {
  const outOfStock = !inStock || stockUnavailable;

  // Back-order only rescues an ordinary out-of-stock product: never when the
  // price is unknown and never for a GO ("removed from sale") product.
  const isBackOrder = outOfStock && !priceUnavailable && !removedFromSale && backOrdersAllowed;

  const labelState: AddToCartLabelState = priceUnavailable
    ? 'price-unavailable'
    : outOfStock
      ? isBackOrder
        ? 'back-order'
        : 'out-of-stock'
      : 'available';

  const disabled = priceUnavailable || removedFromSale || (outOfStock && !isBackOrder);

  return { disabled, labelState, isBackOrder };
};
